/**
 * AppPanel Component
 *
 * Global side panel that renders any app page inline using an iframe.
 * Triggered from the AppLauncher "Open as Panel" button or search bar.
 *
 * Features:
 * - Slide-in animation from right
 * - Resizable width (md/lg/xl/full)
 * - Close on Escape key or backdrop click
 * - iframe renders the target route
 * - Header with title, resize controls, and close button
 * - Minimize to taskbar (Windows-style) - PRESERVES IFRAME STATE
 * - Focus mode hides other UI elements
 * - Multiple panels can be active simultaneously (hidden panels keep iframe mounted)
 */

import React, { useEffect, useCallback } from 'react';
import { X, Maximize2, Minimize2, ExternalLink, Focus, ChevronRight } from 'lucide-react';
import { useAppPanelStore, type ActiveAppPanel, type PanelWidth } from '@shared/store/useAppPanelStore';

const WIDTH_CLASSES: Record<string, string> = {
  md: 'w-[480px]',
  lg: 'w-[640px]',
  xl: 'w-[800px]',
  full: 'w-[50vw]',
  fullscreen: 'w-screen',
};

const NEXT_WIDTH: Record<string, PanelWidth> = {
  md: 'lg',
  lg: 'xl',
  xl: 'full',
  full: 'md',
  fullscreen: 'md',
};

/**
 * Individual Panel Iframe - renders a single panel's iframe
 * Hidden panels are kept mounted but visually hidden to preserve state
 */
interface PanelIframeProps {
  panel: ActiveAppPanel;
  isVisible: boolean;
}

/** Header height in pixels - must match VisiblePanelChrome header */
const HEADER_HEIGHT = 44;

const PanelIframe: React.FC<PanelIframeProps> = ({ panel, isVisible }) => {
  const iframeUrl = `${window.location.origin}${panel.route}`;

  return (
    <div
      className={`
        fixed top-0 right-0 h-full
        transition-all duration-300 ease-out
        ${WIDTH_CLASSES[panel.width]}
        ${isVisible ? 'z-50' : 'z-[-1] pointer-events-none'}
      `}
      style={{
        visibility: isVisible ? 'visible' : 'hidden',
        opacity: isVisible ? 1 : 0,
        // Add padding for header when visible to prevent content clipping
        paddingTop: isVisible ? HEADER_HEIGHT : 0,
      }}
      data-panel-id={panel.id}
      data-panel-hidden={!isVisible}
    >
      <iframe
        src={iframeUrl}
        className="w-full h-full border-0 bg-dark-bg"
        title={panel.title || 'App Panel'}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
      />
    </div>
  );
};

/**
 * Visible Panel Chrome - renders header and backdrop for the visible panel
 */
interface VisiblePanelChromeProps {
  panel: ActiveAppPanel;
  isFocusMode: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onToggleFocusMode: () => void;
  onSetWidth: (width: PanelWidth) => void;
}

const VisiblePanelChrome: React.FC<VisiblePanelChromeProps> = ({
  panel,
  isFocusMode,
  onClose,
  onMinimize,
  onToggleFocusMode,
  onSetWidth,
}) => {
  const handleExpandFullPage = useCallback(() => {
    if (panel.width === 'fullscreen') {
      onSetWidth('xl');
    } else {
      onSetWidth('fullscreen');
    }
  }, [panel.width, onSetWidth]);

  const handleResize = useCallback(() => {
    onSetWidth(NEXT_WIDTH[panel.width]);
  }, [panel.width, onSetWidth]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 ${
          isFocusMode ? 'bg-black/70' : 'bg-black/40'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Header - positioned over the iframe */}
      <div
        className={`
          fixed top-0 right-0 z-50
          bg-dark-bg border-l border-dark-border shadow-2xl shadow-black/30
          flex flex-col
          transition-all duration-300 ease-out
          ${WIDTH_CLASSES[panel.width]}
        `}
        role="dialog"
        aria-label={panel.title || 'App Panel'}
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-dark-border/50 bg-dark-surface shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-medium text-dark-text truncate">
              {panel.title || 'Panel'}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* Focus mode toggle */}
            <button
              onClick={onToggleFocusMode}
              className={`p-1.5 rounded-lg transition-colors ${
                isFocusMode
                  ? 'text-brand-400 bg-brand-500/20 hover:bg-brand-500/30'
                  : 'text-dark-muted hover:text-dark-text hover:bg-dark-bg'
              }`}
              title={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
              aria-label={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
            >
              <Focus size={15} />
            </button>
            {/* Minimize to taskbar */}
            <button
              onClick={onMinimize}
              className="p-1.5 rounded-lg text-dark-muted hover:text-dark-text hover:bg-dark-bg transition-colors"
              title="Minimize to taskbar (preserves page state)"
              aria-label="Minimize panel to taskbar"
            >
              <ChevronRight size={15} />
            </button>
            {/* Expand to full page overlay */}
            <button
              onClick={handleExpandFullPage}
              className={`p-1.5 rounded-lg transition-colors ${
                panel.width === 'fullscreen'
                  ? 'text-brand-400 bg-brand-500/20 hover:bg-brand-500/30'
                  : 'text-dark-muted hover:text-dark-text hover:bg-dark-bg'
              }`}
              title={panel.width === 'fullscreen' ? 'Exit full page' : 'Expand to full page'}
              aria-label={panel.width === 'fullscreen' ? 'Exit full page mode' : 'Expand to full page'}
            >
              <ExternalLink size={15} />
            </button>
            {/* Resize */}
            <button
              onClick={handleResize}
              className="p-1.5 rounded-lg text-dark-muted hover:text-dark-text hover:bg-dark-bg transition-colors"
              title={`Resize (${panel.width})`}
              aria-label="Resize panel"
            >
              {panel.width === 'full' ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
            {/* Close */}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-dark-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
              title="Close panel"
              aria-label="Close panel"
            >
              <X size={15} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * AppPanel - Main component that manages all active panels
 *
 * Renders:
 * - All active panel iframes (visible and hidden)
 * - Chrome (header/backdrop) only for the visible panel
 *
 * Hidden panels keep their iframes mounted to preserve state
 */
export const AppPanel: React.FC = () => {
  const {
    activePanels,
    visiblePanelId,
    isFocusMode,
    closePanel,
    setWidth,
    minimizePanel,
    toggleFocusMode,
  } = useAppPanelStore();

  // Get the currently visible panel
  const visiblePanel = visiblePanelId
    ? activePanels.find(p => p.id === visiblePanelId)
    : null;

  // Close on Escape
  useEffect(() => {
    if (!visiblePanel) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visiblePanel, closePanel]);

  // Don't render anything if inside an iframe (prevents nesting)
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  if (isInIframe) return null;

  // Don't render if no active panels
  if (activePanels.length === 0) return null;

  return (
    <>
      {/* Render all panel iframes (visible and hidden) */}
      {activePanels.map(panel => (
        <PanelIframe
          key={panel.id}
          panel={panel}
          isVisible={panel.id === visiblePanelId}
        />
      ))}

      {/* Render chrome (header/backdrop) only for visible panel */}
      {visiblePanel && (
        <VisiblePanelChrome
          panel={visiblePanel}
          isFocusMode={isFocusMode}
          onClose={closePanel}
          onMinimize={minimizePanel}
          onToggleFocusMode={toggleFocusMode}
          onSetWidth={setWidth}
        />
      )}
    </>
  );
};

export default AppPanel;
